// ─── pages/ListView.jsx ──────────────────────────────────────────────────────
import { useState, useEffect } from "react";
import config from "../config";
import { LabelPill, CardModal } from "../Create_plan_Trello/Shared.jsx";

// ─── Design tokens (same as BoardView) ───────────────────────────────────────
const C = {
  bg:         "#0a1628",
  surface:    "#152438",
  surfaceHov: "#1c2f45",
  border:     "#1a3050",
  border2:    "#1a3050",
  borderHov:  "#2d5a8a",
  accent:     "#579dff",
  text:       "#dce8f5",
  textMuted:  "#4a6a8a",
  textDim:    "#6b8caf",
  green:      "#4bce97",
};

// ─── Current user helper ──────────────────────────────────────────────────────
const getUser = () => {
  try {
    const admin = localStorage.getItem("admin");
    if (admin) {
      const parsed = JSON.parse(admin);
      return parsed.username || parsed.fullNames || "System";
    }
    const user = localStorage.getItem("user");
    if (user) return JSON.parse(user).username || "System";
  } catch {}
  return "System";
};

// ─── Serialization (same as BoardView) ───────────────────────────────────────
const toApiCard = (card) => ({
  id:          card.id ?? 0,
  colId:       card.colId,
  title:       card.title?.trim() || "",
  description: card.desc ?? card.description ?? "",
  labels:      Array.isArray(card.labels) ? card.labels.join(",") : (card.labels ?? ""),
  checklist:   Array.isArray(card.checklist) ? JSON.stringify(card.checklist) : (card.checklist ?? "[]"),
  archived:    card.archived ?? false,
  createdBy:   card.createdBy || getUser(),
  createdAt:   card.createdAt
                 ? (typeof card.createdAt === "number" ? new Date(card.createdAt).toISOString() : card.createdAt)
                 : new Date().toISOString(),
});

const fromApiCard = (card) => ({
  ...card,
  desc: card.description ?? "",
  labels: card.labels ? card.labels.split(",").map((s) => s.trim()).filter(Boolean) : [],
  checklist: (() => { try { return JSON.parse(card.checklist || "[]"); } catch { return []; } })(),
});

export default function ListView() {
  const [columns,  setColumns]  = useState([]);
  const [cards,    setCards]    = useState([]);
  const [search,   setSearch]   = useState("");
  const [openCard, setOpenCard] = useState(null);
  const [loading,  setLoading]  = useState(true);

  // ── Fetch ─────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const [colsRes, cardsRes] = await Promise.all([
          fetch(`${config.API_BASE_URL}/boards`),
          fetch(`${config.API_BASE_URL}/cards`),
        ]);
        if (!colsRes.ok || !cardsRes.ok) throw new Error("Failed to fetch");

        const currentUser = getUser();
        const allCols     = (await colsRes.json()).filter((col) => col.createdBy === currentUser);
        const colIds      = new Set(allCols.map((c) => c.id));
        const allCards    = (await cardsRes.json()).map(fromApiCard).filter((c) => colIds.has(c.colId));

        setColumns(allCols);
        setCards(allCards);
      } catch (err) {
        console.error(err);
        alert("Failed to load data. Refresh the page.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ── Card actions (synced to API) ──────────────────────────────────
  const updateCard = async (updated) => {
    try {
      const res = await fetch(`${config.API_BASE_URL}/cards/${updated.id}`, {
        method:  "PUT",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(toApiCard(updated)),
      });
      if (!res.ok) throw new Error();
      const saved = fromApiCard(await res.json());
      setCards((p) => p.map((c) => (c.id === updated.id ? saved : c)));
      if (openCard?.id === updated.id) setOpenCard(saved);
    } catch {
      alert("Failed to update card.");
    }
  };

  const archiveCard = (id) =>
    setCards((p) => p.map((c) => (c.id === id ? { ...c, archived: !c.archived } : c)));

  const deleteCard = async (id) => {
    try {
      const res = await fetch(`${config.API_BASE_URL}/cards/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error();
      setCards((p) => p.filter((c) => c.id !== id));
      setOpenCard(null);
    } catch {
      alert("Failed to delete card.");
    }
  };

  const activeCards = cards.filter((c) => !c.archived);
  const filtered    = search.trim()
    ? activeCards.filter((c) => c.title?.toLowerCase().includes(search.toLowerCase()))
    : activeCards;

  if (loading) {
    return (
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: C.textMuted, fontSize: 14 }}>Loading list…</span>
      </div>
    );
  }

  return (
    <>
      <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>

        {/* Search bar */}
        <div style={{ marginBottom: 20 }}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="🔍  Search cards…"
            style={{
              background:   C.surface,
              border:       `1px solid ${C.border2}`,
              borderRadius: 8,
              padding:      "9px 14px",
              fontSize:     13,
              color:        C.text,
              outline:      "none",
              fontFamily:   "inherit",
              width:        280,
            }}
            onFocus={(e) => (e.target.style.borderColor = C.accent)}
            onBlur={(e)  => (e.target.style.borderColor = C.border2)}
          />
        </div>

        {/* Empty state */}
        {columns.length === 0 && (
          <div style={{ textAlign: "center", color: C.textMuted, marginTop: 60 }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
            <p style={{ margin: 0, fontSize: 14 }}>No lists found for your account.</p>
            <p style={{ margin: "6px 0 0", fontSize: 12 }}>Switch to Board View to create your first list.</p>
          </div>
        )}

        {/* Columns */}
        {columns.map((col) => {
          const colCards = filtered.filter((c) => c.colId === col.id);
          if (colCards.length === 0) return null;

          return (
            <div key={col.id} style={{ marginBottom: 28 }}>
              {/* Column header */}
              <div style={{
                display: "flex", alignItems: "center", gap: 10,
                marginBottom: 10, paddingBottom: 8,
                borderBottom: `1px solid ${C.border}`,
              }}>
                <div style={{ width: 10, height: 10, borderRadius: "50%", background: col.color }} />
                <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{col.title}</span>
                <span style={{
                  background: C.surfaceHov, color: C.textMuted,
                  fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 10,
                }}>
                  {colCards.length}
                </span>
              </div>

              {/* Cards */}
              {colCards.map((card) => {
                const done     = card.checklist.filter((i) => i.checked).length;
                const total    = card.checklist.length;
                const progress = total > 0 ? Math.round((done / total) * 100) : 0;

                return (
                  <div
                    key={card.id}
                    onClick={() => setOpenCard(card)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background  = C.surfaceHov;
                      e.currentTarget.style.borderColor = C.borderHov;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background  = C.surface;
                      e.currentTarget.style.borderColor = C.border;
                    }}
                    style={{
                      display: "flex", alignItems: "center", gap: 14,
                      padding: "11px 14px",
                      background: C.surface, border: `1px solid ${C.border}`,
                      borderRadius: 8, marginBottom: 6,
                      cursor: "pointer", transition: "all .12s",
                    }}
                  >
                    {/* Title & labels */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 13, fontWeight: 500, color: C.text,
                        marginBottom: card.labels.length ? 6 : 0,
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                      }}>
                        {card.title}
                      </div>
                      {card.labels.length > 0 && (
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                          {card.labels.map((l) => <LabelPill key={l} color={l} />)}
                        </div>
                      )}
                    </div>

                    {/* Progress bar */}
                    {progress > 0 && (
                      <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                        <div style={{ width: 90, height: 5, background: C.border, borderRadius: 3, overflow: "hidden" }}>
                          <div style={{
                            height: "100%", width: `${progress}%`,
                            background: progress === 100 ? C.green : C.accent,
                            borderRadius: 3, transition: "width .3s",
                          }} />
                        </div>
                        <span style={{
                          fontSize: 11, fontWeight: 700, width: 34, textAlign: "right",
                          color: progress === 100 ? C.green : C.textMuted,
                        }}>
                          {progress}%
                        </span>
                      </div>
                    )}

                    {/* Has description icon */}
                    {card.desc && (
                      <span style={{ fontSize: 12, color: C.textDim, flexShrink: 0 }}>📝</span>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* ✅ Card Modal — bukas kapag may openCard */}
      {openCard && (
        <CardModal
          card={openCard}
          columns={columns}
          onClose={() => setOpenCard(null)}
          onUpdate={updateCard}
          onArchive={archiveCard}
          onDelete={deleteCard}
        />
      )}
    </>
  );
}